/**
 * CaseExpensesTable - Case expense management table with filtering and actions
 */

import { PlusIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { DataTable } from '@/components/common/DataTable/DataTable';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  CaseExpenseStatus,
  CaseExpenseType,
  ReadCaseExpense,
} from '@/services/api/generated/caseExpensesService';
import {
  useCaseExpenseDestroyMutation,
  useCaseExpenseListQuery,
} from '@/services/api/generated/caseExpensesService';

import { DeleteConfirmDialog } from '../shared/DeleteConfirmDialog';
import { LedgerEmptyState } from '../shared/LedgerEmptyState';
import { createCaseExpenseColumns } from './caseExpenseColumns';

type StatusFilter = 'all' | CaseExpenseStatus;
type TypeFilter = 'all' | CaseExpenseType;

interface CaseExpensesTableProps {
  matterId: string;
  onEditExpense: (expense: ReadCaseExpense) => void;
  onAddExpense?: () => void;
}

export const CaseExpensesTable = ({
  matterId,
  onEditExpense,
  onAddExpense,
}: CaseExpensesTableProps) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [expenseToDelete, setExpenseToDelete] =
    useState<ReadCaseExpense | null>(null);

  const {
    data: expenses = [],
    isLoading,
    isFetching,
  } = useCaseExpenseListQuery({
    matterId,
    status: statusFilter === 'all' ? undefined : statusFilter,
    expenseType: typeFilter === 'all' ? undefined : typeFilter,
  });

  const [deleteExpense, { isLoading: isDeleting }] =
    useCaseExpenseDestroyMutation();

  const handleDelete = useCallback((expense: ReadCaseExpense) => {
    setExpenseToDelete(expense);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!expenseToDelete) return;
    try {
      await deleteExpense({
        matterId,
        id: expenseToDelete.id as string,
      }).unwrap();
      toast.success('Case expense deleted');
      setExpenseToDelete(null);
    } catch {
      toast.error('Failed to delete. Please try again.');
    }
  }, [expenseToDelete, matterId, deleteExpense]);

  const columns = useMemo(
    () =>
      createCaseExpenseColumns({
        onEdit: onEditExpense,
        onDelete: handleDelete,
      }),
    [onEditExpense, handleDelete],
  );

  const hasFilters = statusFilter !== 'all' || typeFilter !== 'all';

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Select
            value={typeFilter}
            onValueChange={(v) => setTypeFilter(v as TypeFilter)}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="POLICE">Police</SelectItem>
              <SelectItem value="MEDICAL">Medical</SelectItem>
              <SelectItem value="MAIL">Mail</SelectItem>
              <SelectItem value="OTHER">Other</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as StatusFilter)}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="UNPAID">Unpaid</SelectItem>
              <SelectItem value="SENT">Sent</SelectItem>
              <SelectItem value="PAID">Paid</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {onAddExpense && (
          <Button
            onClick={onAddExpense}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Add Expense
          </Button>
        )}
      </div>

      {/* Table or Empty State */}
      {expenses.length === 0 && !isLoading ? (
        <LedgerEmptyState
          title="No case expenses found"
          description={
            hasFilters
              ? 'Try adjusting your filters'
              : 'Add a case expense to track firm disbursements.'
          }
          action={
            !hasFilters && onAddExpense
              ? { label: 'Add expense', onClick: onAddExpense }
              : undefined
          }
        />
      ) : (
        <DataTable
          data={expenses}
          columns={columns}
          isLoading={isLoading || isFetching}
          getRowKey={(row) => row.id as string}
          density="compact"
          onRowClick={onEditExpense}
        />
      )}

      {/* Delete confirmation */}
      <DeleteConfirmDialog
        entityType="expense"
        open={expenseToDelete !== null}
        onConfirm={confirmDelete}
        onCancel={() => setExpenseToDelete(null)}
        isDeleting={isDeleting}
      />
    </div>
  );
};
